import React from 'react';
import { Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import {
  ATTEND_STATUS_META,
  CONSULT_RESULT_META,
  ETC_CARD_META,
  CONSULT_TARGET_META,
  TICKET_STATUS_META,
  TooltipContainer,
  TooltipHeader,
  TooltipTitle,
  TooltipSubtitle,
  TooltipBody,
  TooltipRow,
  TooltipStatusBadge,
  TooltipTicketBanner,
  TooltipTicketBadge,
  TooltipLessonHeader,
  TooltipLessonTitle,
  TooltipInfoRow,
  TooltipInfoLabel,
  TooltipInfoValue,
  TooltipConsultContent,
} from './PrivateLessonStyled';
import { getLessonAttendStatus, isTicketExpiring } from './lessonStatus';

// 흰 배경 툴팁 공통 스타일 (기존 카드 4벌에 복붙되어 있던 componentsProps 통합)
const TOOLTIP_COMPONENT_PROPS = {
  popper: { sx: { pointerEvents: 'none' } },
  tooltip: {
    sx: {
      bgcolor: '#fff',
      color: '#333',
      borderRadius: '10px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      border: '1px solid #E0E3E6',
      p: '10px',
      minWidth: '210px',
    },
  },
  arrow: { sx: { color: '#fff', '&::before': { border: '1px solid #E0E3E6' } } },
};

// 그룹레슨 예약 상태 메타
const GROUP_STATUS_META = {
  full: { label: '예약마감', badgeBg: '#A7E3BE', badgeFont: '#247E46' },
  under: { label: '예약미달', badgeBg: '#FFD9A9', badgeFont: '#AA6000' },
  none: { label: '예약없음', badgeBg: '#F6B6A9', badgeFont: '#9E230B' },
  closed: { label: '폐강', badgeBg: '#D1D1D1', badgeFont: '#404040' },
  ended: { label: '종료', badgeBg: '#BFBFBF', badgeFont: '#404040' },
};

const getGroupStatus = event => {
  const reserved = event.reservedMemberCount || 0;
  const attended = event.attendedMemberCount || 0;
  const absent = event.absentedMemberCount || 0;
  const max = event.membersMaxCount || event.maxMemberCount || 0;

  if (event.openYn === 'N' || event.closeDate) return 'closed';
  if (reserved + attended >= max) return 'full';
  if (reserved === 0 && attended === 0 && absent === 0) return 'none';
  return 'under';
};

// 그룹레슨 툴팁 내용
const GroupTooltipContent = ({ event }) => {
  const totalMemberCount =
    (event.reservedMemberCount || 0) + (event.attendedMemberCount || 0) + (event.absentedMemberCount || 0);
  const groupStatus = getGroupStatus(event);
  const statusMeta = GROUP_STATUS_META[groupStatus];
  return (
    <TooltipContainer>
      <TooltipLessonHeader>
        <TooltipLessonTitle>{event.className}</TooltipLessonTitle>
        <TooltipStatusBadge badgeBg={statusMeta.badgeBg} badgeFont={statusMeta.badgeFont}>
          {statusMeta.label}
        </TooltipStatusBadge>
      </TooltipLessonHeader>
      <TooltipBody>
        <TooltipInfoRow>
          <TooltipInfoLabel>시간</TooltipInfoLabel>
          <TooltipInfoValue>{event.startTime} - {event.endTime}</TooltipInfoValue>
        </TooltipInfoRow>
        <TooltipInfoRow>
          <TooltipInfoLabel>강사</TooltipInfoLabel>
          <TooltipInfoValue>{event.trainerName}</TooltipInfoValue>
        </TooltipInfoRow>
        {event.branchExerciseRoomName && (
          <TooltipInfoRow>
            <TooltipInfoLabel>강의실</TooltipInfoLabel>
            <TooltipInfoValue>{event.branchExerciseRoomName}</TooltipInfoValue>
          </TooltipInfoRow>
        )}
        <TooltipInfoRow>
          <TooltipInfoLabel>예약</TooltipInfoLabel>
          <TooltipInfoValue style={{ color: '#0F7F85' }}>{event.reservedMemberCount || 0}명</TooltipInfoValue>
        </TooltipInfoRow>
        <TooltipInfoRow>
          <TooltipInfoLabel>출석</TooltipInfoLabel>
          <TooltipInfoValue style={{ color: '#2E7D32' }}>{event.attendedMemberCount || 0}명</TooltipInfoValue>
        </TooltipInfoRow>
        <TooltipInfoRow>
          <TooltipInfoLabel>결석</TooltipInfoLabel>
          <TooltipInfoValue style={{ color: '#D32F2F' }}>{event.absentedMemberCount || 0}명</TooltipInfoValue>
        </TooltipInfoRow>
        <TooltipInfoRow>
          <TooltipInfoLabel>정원</TooltipInfoLabel>
          <TooltipInfoValue>{totalMemberCount} / {event.membersMaxCount}명</TooltipInfoValue>
        </TooltipInfoRow>
      </TooltipBody>
    </TooltipContainer>
  );
};

// 개인/듀엣 레슨 툴팁 내용 (시안: 회원명+출석상태 / 수강권상태+지점·강의실 / 구분·세션·잔여횟수·전화번호·시간·강사)
const LessonTooltipContent = ({ event, ticketAlertCount, ticketAlertCountUnit, branchName }) => {
  const status = getLessonAttendStatus(event);
  const meta = ATTEND_STATUS_META[status];
  const isDuet = event.courseClassType === 'DUET_LESSON' || event.courseClassType === 'OT';
  const memberName =
    isDuet && event.members?.length > 0
      ? event.members.map(member => member.memberName).filter(Boolean).join(', ')
      : event.memberName || '';
  const hasSessionInfo =
    typeof event.totalSessionCount === 'number' && typeof event.remainingSessionCount === 'number';
  const expiring = hasSessionInfo && isTicketExpiring(event, ticketAlertCount, ticketAlertCountUnit);
  const ticketMeta = TICKET_STATUS_META[expiring ? 'expiring' : 'valid'];
  // 지점명은 "센트리얼 필라테스 [반포점]" 형태라 괄호 라벨만 노출 (없으면 전체 사용)
  const branchLabel = branchName?.match(/\[[^\]]+\]/)?.[0] || branchName;
  const roomText = [branchLabel, event.branchExerciseRoomName].filter(Boolean).join(' ');

  return (
    <TooltipContainer>
      <TooltipLessonHeader>
        <TooltipLessonTitle>{memberName}</TooltipLessonTitle>
        <TooltipStatusBadge badgeBg={meta.tooltipBadgeBg} badgeFont={meta.tooltipBadgeFont}>
          {meta.label}
        </TooltipStatusBadge>
      </TooltipLessonHeader>
      {hasSessionInfo && (
        <TooltipTicketBanner isExpiring={expiring}>
          <TooltipTicketBadge badgeBg={ticketMeta.badgeBg} badgeFont={ticketMeta.badgeFont}>
            {ticketMeta.label}
          </TooltipTicketBadge>
          <span>잔여 {event.remainingSessionCount}회</span>
        </TooltipTicketBanner>
      )}
      <TooltipBody>
        <TooltipInfoRow>
          <TooltipInfoLabel>구분</TooltipInfoLabel>
          <TooltipInfoValue>{event.courseClassType === 'OT' ? 'OT' : isDuet ? '듀엣레슨' : '개인레슨'}</TooltipInfoValue>
        </TooltipInfoRow>
        {hasSessionInfo && (
          <TooltipInfoRow>
            <TooltipInfoLabel>세션</TooltipInfoLabel>
            <TooltipInfoValue isAlert={expiring}>
              {event.remainingSessionCount} / {event.totalSessionCount}회
            </TooltipInfoValue>
          </TooltipInfoRow>
        )}
        {event.memberPhoneNumber && (
          <TooltipInfoRow>
            <TooltipInfoLabel>전화번호</TooltipInfoLabel>
            <TooltipInfoValue>{event.memberPhoneNumber}</TooltipInfoValue>
          </TooltipInfoRow>
        )}
        <TooltipInfoRow>
          <TooltipInfoLabel>시간</TooltipInfoLabel>
          <TooltipInfoValue>
            {event.startTime} - {event.endTime}
          </TooltipInfoValue>
        </TooltipInfoRow>
        {event.trainerName && (
          <TooltipInfoRow>
            <TooltipInfoLabel>강사</TooltipInfoLabel>
            <TooltipInfoValue>{event.trainerName}</TooltipInfoValue>
          </TooltipInfoRow>
        )}
        {roomText && (
          <TooltipInfoRow>
            <TooltipInfoLabel>강의실</TooltipInfoLabel>
            <TooltipInfoValue>{roomText}</TooltipInfoValue>
          </TooltipInfoRow>
        )}
      </TooltipBody>
    </TooltipContainer>
  );
};

// 상담 툴팁 내용 — 개인레슨 툴팁과 같은 문법. 헤더는 이름+결과, 본문은 라벨-값 행.
const ConsultTooltipContent = ({ event }) => {
  const meta = CONSULT_RESULT_META[event.consultResult] || CONSULT_RESULT_META.SCHEDULED;
  const targetLabel = CONSULT_TARGET_META[event.consultType]?.label || CONSULT_TARGET_META.MEMBER.label;
  return (
    <TooltipContainer>
      <TooltipLessonHeader>
        <TooltipLessonTitle>{event.memberName || '-'}</TooltipLessonTitle>
        <TooltipStatusBadge badgeBg={meta.tooltipBadgeBg} badgeFont={meta.tooltipBadgeFont}>
          {meta.label}
        </TooltipStatusBadge>
      </TooltipLessonHeader>
      <TooltipBody>
        {event.className && (
          <TooltipInfoRow>
            <TooltipInfoLabel>제목</TooltipInfoLabel>
            <TooltipInfoValue>{event.className}</TooltipInfoValue>
          </TooltipInfoRow>
        )}
        <TooltipInfoRow>
          <TooltipInfoLabel>구분</TooltipInfoLabel>
          <TooltipInfoValue>상담 · {targetLabel}</TooltipInfoValue>
        </TooltipInfoRow>
        {event.memberPhoneNumber && (
          <TooltipInfoRow>
            <TooltipInfoLabel>전화번호</TooltipInfoLabel>
            <TooltipInfoValue>{event.memberPhoneNumber}</TooltipInfoValue>
          </TooltipInfoRow>
        )}
        <TooltipInfoRow>
          <TooltipInfoLabel>시간</TooltipInfoLabel>
          <TooltipInfoValue>
            {event.startTime} - {event.endTime}
          </TooltipInfoValue>
        </TooltipInfoRow>
        {event.trainerName && (
          <TooltipInfoRow>
            <TooltipInfoLabel>담당자</TooltipInfoLabel>
            <TooltipInfoValue>{event.trainerName}</TooltipInfoValue>
          </TooltipInfoRow>
        )}
        {event.consultContentPreview && (
          <TooltipInfoRow>
            <TooltipInfoLabel>내용</TooltipInfoLabel>
            <TooltipConsultContent>{event.consultContentPreview}</TooltipConsultContent>
          </TooltipInfoRow>
        )}
      </TooltipBody>
    </TooltipContainer>
  );
};

// 기타 일정 툴팁 — 제목이 헤더, 본문은 시간·담당자·내용.
const EtcTooltipContent = ({ event }) => (
  <TooltipContainer>
    <TooltipLessonHeader>
      <TooltipLessonTitle>{event.className || '-'}</TooltipLessonTitle>
      <TooltipStatusBadge badgeBg={ETC_CARD_META.tooltipBadgeBg} badgeFont={ETC_CARD_META.tooltipBadgeFont}>
        기타
      </TooltipStatusBadge>
    </TooltipLessonHeader>
    <TooltipBody>
      <TooltipInfoRow>
        <TooltipInfoLabel>시간</TooltipInfoLabel>
        <TooltipInfoValue>
          {event.startTime} - {event.endTime}
        </TooltipInfoValue>
      </TooltipInfoRow>
      {event.trainerName && (
        <TooltipInfoRow>
          <TooltipInfoLabel>담당자</TooltipInfoLabel>
          <TooltipInfoValue>{event.trainerName}</TooltipInfoValue>
        </TooltipInfoRow>
      )}
      {event.memo && (
        <TooltipInfoRow>
          <TooltipInfoLabel>내용</TooltipInfoLabel>
          <TooltipConsultContent>{event.memo}</TooltipConsultContent>
        </TooltipInfoRow>
      )}
    </TooltipBody>
  </TooltipContainer>
);

/**
 * 개인/듀엣/그룹/상담/기타 공용 hover 툴팁 (일간 카드·주간 그리드 카드·주/월 패널 항목 공통)
 */
const LessonHoverTooltip = ({ event, ticketAlertCount, ticketAlertCountUnit, placement = 'right', children }) => {
  // 스케줄은 항상 선택된 단일 지점 기준이라 지점명은 전역 상태에서 읽는다 (목록 응답에는 없음)
  const branchName = useSelector(state => state.user?.user?.branch?.branchName);

  const content =
    event.courseClassType === 'CONSULTATION' ? (
      <ConsultTooltipContent event={event} />
    ) : event.courseClassType === 'ETC' ? (
      <EtcTooltipContent event={event} />
    ) : event.courseClassType === 'GROUP_LESSON' ? (
      <GroupTooltipContent event={event} />
    ) : (
      <LessonTooltipContent event={event} ticketAlertCount={ticketAlertCount} ticketAlertCountUnit={ticketAlertCountUnit} branchName={branchName} />
    );

  return (
    <Tooltip title={content} arrow placement={placement} componentsProps={TOOLTIP_COMPONENT_PROPS}>
      {children}
    </Tooltip>
  );
};

export default LessonHoverTooltip;
